import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import { type Lead, getSourceAnalytics } from "@/hooks/useAdmin";
import { BarChart3, PieChart as PieIcon } from "lucide-react";
import { useState } from "react";

const COLORS = [
  "#0d9488", // teal-600
  "#059669", // emerald-600
  "#0284c7", // sky-600
  "#7c3aed", // violet-600
  "#dc2626", // red-600
  "#ea580c", // orange-600
  "#2563eb", // blue-600
  "#0891b2", // cyan-600
  "#65a30d", // lime-600
  "#9333ea", // purple-600
];

interface LeadSourceAnalyticsProps {
  leads: Lead[];
}

export default function LeadSourceAnalytics({ leads }: LeadSourceAnalyticsProps) {
  const [chartType, setChartType] = useState<"bar" | "pie">("bar");

  const analytics = useMemo(() => getSourceAnalytics(leads), [leads]);

  const totalLeads = leads.length;

  const barData = analytics.map((item) => ({
    name: item.source.length > 24 ? item.source.slice(0, 24) + "..." : item.source,
    fullName: item.source,
    count: item.count,
    converted: item.converted,
    new: item.new,
  }));

  const pieData = analytics.map((item) => ({
    name: item.source,
    value: item.count,
  }));

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: { fullName: string; count: number; converted: number; new: number } }> }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const conversionRate = data.count > 0 ? Math.round((data.converted / data.count) * 100) : 0;
      return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
          <div className="font-semibold text-slate-800 mb-1">{data.fullName}</div>
          <div className="text-slate-600">Total: <span className="font-semibold text-slate-900">{data.count}</span></div>
          <div className="text-slate-600">Converted: <span className="font-semibold text-emerald-600">{data.converted}</span> ({conversionRate}%)</div>
          <div className="text-slate-600">New: <span className="font-semibold text-amber-600">{data.new}</span></div>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ name: string; value: number }> }) => {
    if (active && payload && payload.length) {
      const pct = totalLeads > 0 ? Math.round((payload[0].value / totalLeads) * 100) : 0;
      return (
        <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
          <div className="font-semibold text-slate-800">{payload[0].name}</div>
          <div className="text-slate-600">Leads: <span className="font-semibold text-slate-900">{payload[0].value}</span> ({pct}%)</div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold text-slate-900">Lead Source Analytics</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {totalLeads} total leads across {analytics.length} sources
          </p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          <button
            onClick={() => setChartType("bar")}
            className={`p-1.5 rounded-md transition-colors ${
              chartType === "bar" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            title="Bar Chart"
          >
            <BarChart3 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setChartType("pie")}
            className={`p-1.5 rounded-md transition-colors ${
              chartType === "pie" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
            title="Pie Chart"
          >
            <PieIcon className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {chartType === "bar" ? (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis
                dataKey="name"
                type="category"
                width={140}
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20}>
                {barData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="h-56 w-56 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 w-full">
            <div className="space-y-2 max-h-56 overflow-y-auto pr-2">
              {analytics.map((item, idx) => {
                const pct = totalLeads > 0 ? Math.round((item.count / totalLeads) * 100) : 0;
                return (
                  <div key={item.source} className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-700 truncate">{item.source}</span>
                        <span className="text-xs font-semibold text-slate-900 ml-2">{item.count}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1">
                        <div
                          className="h-1.5 rounded-full"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: COLORS[idx % COLORS.length],
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-500 shrink-0">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}